import {
  BookOpen,
  Brain,
  Headphones,
  Leaf,
  Music2,
  Radio,
  Sparkles,
  Waves,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import TopbarInner from "./components/topbar/TopbarInner";
import { Helmet } from "react-helmet-async";
import { goBack } from "./utils/goBack";

export default function FrequencesPage({ onThemeToggle, showThemeDropdown = true, isPremium = false, longestStreak = 0 }) {
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <title>Sons &amp; fréquences — comprendre ce que vous écoutez | Harmonauria</title>
        <meta name="description" content="Ondes cérébrales, rythmes et paysages sonores : quelques repères simples pour comprendre ce que vous écoutez dans Harmonauria et pourquoi cela agit sur la détente." />
        <link rel="canonical" href="https://harmonauria.fr/frequences" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Sons &amp; fréquences — comprendre ce que vous écoutez" />
        <meta property="og:description" content="Des repères simples sur les sons, les rythmes et les paysages sonores d'Harmonauria." />
        <meta property="og:url" content="https://harmonauria.fr/frequences" />
      </Helmet>

<TopbarInner
  title="Sons & fréquences"
  onBack={() => goBack(navigate)}
  onThemeToggle={onThemeToggle}
  titleColor="#36495B"
  showThemeDropdown={showThemeDropdown}
  isPremium={isPremium}
  longestStreak={longestStreak}
/>

      <main className="frequences-page">
        <header className="page-hero">
          <p className="page-hero__eyebrow">
            Comprendre ce que vous écoutez
          </p>

          <h1 className="page-hero__title">
            Sons &amp; fréquences
          </h1>

          <p className="page-hero__lead">
            Quelques repères simples sur les sons, les rythmes et les
            paysages sonores d’Harmonauria.
          </p>
        </header>

        <Link
          to="/science"
          state={{ from: "/frequences" }}
          className="frequency-science-link"
        >
          <span className="frequency-science-link__icon" aria-hidden="true">
            <BookOpen size={18} />
          </span>

          <span className="frequency-science-link__content">
            <strong>Découvrir les sources scientifiques</strong>
            <span>Consultez les études qui éclairent les pratiques proposées dans Harmonauria.</span>
          </span>

          <span className="frequency-science-link__arrow" aria-hidden="true">→</span>
        </Link>

        <article className="frequency-content">
          {/* Ondes cérébrales */}

          <section
            className="frequency-section"
            aria-labelledby="frequency-brainwaves"
          >
            <div className="frequency-heading">
              <span className="frequency-icon" aria-hidden>
                <Brain size={19} />
              </span>

              <h2
                id="frequency-brainwaves"
                className="frequency-subtitle"
              >
                Ondes cérébrales et sons binauraux
              </h2>
            </div>

            <p className="frequency-text">
              Lorsque deux sons légèrement différents sont envoyés
              séparément dans chaque oreille, une pulsation correspondant
              à leur écart peut être perçue. C’est ce que l’on appelle un{" "}
              <strong>battement binaural</strong>.
            </p>

            <p className="frequency-text">
              Les termes Delta, Thêta, Alpha et Bêta désignent des plages
              d’activité cérébrale observées dans différents contextes.
              Ils constituent des repères, mais ne garantissent pas à eux
              seuls un état précis.
            </p>

            <div
              className="frequency-table-wrap"
              role="region"
              aria-label="Plages d’ondes cérébrales"
              tabIndex={0}
            >
              <table className="frequency-table">
                <thead>
                  <tr>
                    <th>Type d’onde</th>
                    <th>Fréquence</th>
                    <th>Contexte souvent associé</th>
                  </tr>
                </thead>

                <tbody>
                  <tr>
                    <td>
                      <strong>Delta</strong>
                    </td>
                    <td>0,5 à 4 Hz</td>
                    <td>Sommeil profond</td>
                  </tr>

                  <tr>
                    <td>
                      <strong>Thêta</strong>
                    </td>
                    <td>4 à 8 Hz</td>
                    <td>Somnolence et méditation profonde</td>
                  </tr>

                  <tr>
                    <td>
                      <strong>Alpha</strong>
                    </td>
                    <td>8 à 13 Hz</td>
                    <td>Détente éveillée</td>
                  </tr>

                  <tr>
                    <td>
                      <strong>Bêta</strong>
                    </td>
                    <td>13 à 30 Hz</td>
                    <td>Éveil et activité mentale soutenue</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="frequency-text frequency-text--last">
              Plusieurs recherches ont étudié l’utilisation des battements
              binauraux pour accompagner la détente ou réduire l’anxiété
              dans certains contextes. Les résultats restent variables
              selon les personnes et les protocoles employés.
            </p>
          </section>

          {/* Tons isochrones */}

          <section
            className="frequency-section"
            aria-labelledby="frequency-isochrones"
          >
            <div className="frequency-heading">
              <span className="frequency-icon" aria-hidden>
                <Radio size={19} />
              </span>

              <h2
                id="frequency-isochrones"
                className="frequency-subtitle"
              >
                Les tons isochrones
              </h2>
            </div>

            <p className="frequency-text">
              Les <strong>tons isochrones</strong> sont des impulsions
              sonores régulières qui forment une pulsation claire et
              répétitive. Contrairement aux battements binauraux, ils
              ne reposent pas sur l’écoute séparée de deux fréquences.
            </p>

            <p className="frequency-text frequency-text--last">
              Ils peuvent accompagner un moment de détente, de
              concentration ou de préparation au sommeil. La recherche
              sur leurs effets reste toutefois limitée : ils sont donc
              proposés comme une expérience sensorielle, sans promesse
              thérapeutique.
            </p>
          </section>

          {/* Solfeggio */}

          <section
            className="frequency-section frequency-section--soft"
            aria-labelledby="frequency-solfeggio"
          >
            <div className="frequency-heading">
              <span
                className="frequency-icon frequency-icon--soft"
                aria-hidden
              >
                <Sparkles size={19} />
              </span>

              <h2
                id="frequency-solfeggio"
                className="frequency-subtitle"
              >
                Les fréquences dites Solfeggio
              </h2>
            </div>

            <p className="frequency-text">
              Certaines fréquences sont associées, dans différentes
              traditions sonores contemporaines, à des intentions
              symboliques ou émotionnelles.
            </p>

            <p className="frequency-text">
              Ces associations relèvent avant tout d’une démarche
              musicale et expérientielle. Elles ne correspondent pas
              à des effets médicaux démontrés.
            </p>

<div
  className="frequency-table-wrap frequency-table-wrap--traditions"
  role="region"
  aria-label="Fréquences et associations traditionnelles"
  tabIndex={0}
>
<table className="frequency-table frequency-table--traditions">
  <thead>
    <tr>
      <th>Fréquence</th>
      <th>Association traditionnelle</th>
      <th>Chakra associé</th>
    </tr>
  </thead>

  <tbody>
    <tr>
      <td><strong>136,10 Hz</strong></td>
      <td>Fréquence Om, apaisement</td>
      <td>Cœur</td>
    </tr>

    <tr>
      <td><strong>174 Hz</strong></td>
      <td>Sécurité et ancrage</td>
      <td>Racine</td>
    </tr>

    <tr>
      <td><strong>256 Hz</strong></td>
      <td>Harmonie naturelle</td>
      <td>Racine</td>
    </tr>

    <tr>
      <td><strong>285 Hz</strong></td>
      <td>Présence corporelle</td>
      <td>Racine / Sacré</td>
    </tr>

    <tr>
      <td><strong>396 Hz</strong></td>
      <td>Lâcher-prise</td>
      <td>Racine</td>
    </tr>

    <tr>
      <td><strong>417 Hz</strong></td>
      <td>Transformation</td>
      <td>Sacré</td>
    </tr>

    <tr>
      <td><strong>432 Hz</strong></td>
      <td>Équilibre vibratoire</td>
      <td>Global / Cœur</td>
    </tr>

    <tr>
      <td><strong>444 Hz</strong></td>
      <td>Clarté</td>
      <td>Cœur</td>
    </tr>

    <tr>
      <td><strong>528 Hz</strong></td>
      <td>Harmonie</td>
      <td>Plexus solaire</td>
    </tr>

    <tr>
      <td><strong>639 Hz</strong></td>
      <td>Relations et communication</td>
      <td>Cœur</td>
    </tr>

    <tr>
      <td><strong>741 Hz</strong></td>
      <td>Clarté mentale</td>
      <td>Gorge</td>
    </tr>

    <tr>
      <td><strong>852 Hz</strong></td>
      <td>Retour à soi</td>
      <td>Troisième œil</td>
    </tr>

    <tr>
      <td><strong>888 Hz</strong></td>
      <td>Expansion</td>
      <td>Troisième œil / Couronne</td>
    </tr>

    <tr>
      <td><strong>963 Hz</strong></td>
      <td>Connexion</td>
      <td>Couronne</td>
    </tr>

    <tr>
      <td><strong>1 000 Hz</strong></td>
      <td>Ouverture</td>
      <td>Couronne</td>
    </tr>
  </tbody>
</table>
            </div>
          </section>

          {/* Instruments */}

          <section
            className="frequency-section"
            aria-labelledby="frequency-instruments"
          >
            <div className="frequency-heading">
              <span className="frequency-icon" aria-hidden>
                <Music2 size={19} />
              </span>

              <h2
                id="frequency-instruments"
                className="frequency-subtitle"
              >
                Instruments et textures sonores
              </h2>
            </div>

            <p className="frequency-text">
              Les instruments et les textures donnent à chaque paysage
              sonore sa couleur, son mouvement et sa profondeur.
            </p>

            <ul className="frequency-list frequency-list--columns">
              <li>
                <strong>Bols tibétains</strong>
                <span>Résonance, profondeur et ancrage.</span>
              </li>

              <li>
                <strong>Piano doux</strong>
                <span>Émotion, simplicité et fluidité.</span>
              </li>

              <li>
                <strong>Cordes</strong>
                <span>Chaleur organique et mouvement.</span>
              </li>

              <li>
                <strong>Synthétiseurs analogiques</strong>
                <span>Immersion et profondeur enveloppante.</span>
              </li>

              <li>
                <strong>Textures atmosphériques</strong>
                <span>Espace, lenteur et sensation de suspension.</span>
              </li>

              <li>
                <strong>Sons naturels</strong>
                <span>Présence, familiarité et retour au vivant.</span>
              </li>
            </ul>
          </section>

          {/* Sons naturels */}

          <section
            className="frequency-section"
            aria-labelledby="frequency-nature"
          >
            <div className="frequency-heading">
              <span className="frequency-icon" aria-hidden>
                <Leaf size={19} />
              </span>

              <h2
                id="frequency-nature"
                className="frequency-subtitle"
              >
                Les sons naturels
              </h2>
            </div>

            <p className="frequency-text">
              Les sons de la nature peuvent former un paysage familier
              et enveloppant. Ils sont souvent utilisés pour masquer les
              distractions, accompagner le repos ou créer une sensation
              de présence.
            </p>

            <ul className="frequency-list frequency-list--columns">
              <li>
                <strong>Pluie</strong>
                <span>Régularité et enveloppement.</span>
              </li>

              <li>
                <strong>Ruisseau</strong>
                <span>Fluidité et mouvement doux.</span>
              </li>

              <li>
                <strong>Vagues</strong>
                <span>Rythme lent et respiration.</span>
              </li>

              <li>
                <strong>Feu crépitant</strong>
                <span>Chaleur et sensation de cocon.</span>
              </li>

              <li>
                <strong>Oiseaux</strong>
                <span>Légèreté et connexion au vivant.</span>
              </li>

              <li>
                <strong>Forêt nocturne</strong>
                <span>Profondeur, lenteur et repos.</span>
              </li>

              <li>
                <strong>Océan</strong>
                <span>Horizon et sensation d’espace.</span>
              </li>

              <li>
                <strong>Chant des baleines</strong>
                <span>Immersion et vibrations profondes.</span>
              </li>

              <li>
                <strong>Vent</strong>
                <span>Continuité et mouvement naturel.</span>
              </li>

              <li>
                <strong>Ronronnement</strong>
                <span>Grave doux et présence rassurante.</span>
              </li>
            </ul>
          </section>

          {/* Résumé */}

          <section
            className="frequency-section frequency-section--last"
            aria-labelledby="frequency-summary"
          >
            <div className="frequency-heading">
              <span className="frequency-icon" aria-hidden>
                <Waves size={19} />
              </span>

              <h2
                id="frequency-summary"
                className="frequency-subtitle"
              >
                Choisir ce qui vous convient
              </h2>
            </div>

            <p className="frequency-text">
              Il n’existe pas une fréquence universelle adaptée à tout
              le monde. Votre ressenti reste le meilleur point de repère.
            </p>

            <ol className="frequency-steps">
              <li>
                <span>1</span>

                <div>
                  <strong>Commencez par votre besoin</strong>
                  <p>
                    Se relaxer, ralentir, respirer ou simplement mettre le
                    bruit extérieur à distance.
                  </p>
                </div>
              </li>

              <li>
                <span>2</span>

                <div>
                  <strong>Choisissez une ambiance</strong>
                  <p>
                    Naturelle, musicale, profonde ou plus minimaliste.
                  </p>
                </div>
              </li>

              <li>
                <span>3</span>

                <div>
                  <strong>Écoutez vos sensations</strong>
                  <p>
                    Gardez ce qui vous apaise et laissez de côté ce qui
                    ne vous convient pas.
                  </p>
                </div>
              </li>
            </ol>

            <div className="frequency-closing">
              <Headphones size={20} aria-hidden />

              <p>
                L’essentiel n’est pas de rechercher la fréquence parfaite,
                mais de trouver un paysage sonore dans lequel vous pouvez
                enfin souffler.
              </p>
            </div>
          </section>
        </article>

        <style>{`
          .frequences-page,
          .frequences-page * {
            box-sizing: border-box;
          }

          .frequences-page {
            width: 100%;
            min-height: 100%;
            margin-top: 88px;
            padding: 0 20px 100px;

            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 42px;
          }

          .frequency-science-link {
width: min(100%, 920px);
  margin-top: -16px;
  padding: 17px 20px;
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr) auto;
  align-items: center;
  gap: 14px;
  color: #4f7067;
  background: rgba(255, 255, 255, 0.82);
  border: 1px solid rgba(97, 131, 122, 0.15);
  border-radius: 18px;
  box-shadow: 0 12px 30px rgba(54, 73, 91, 0.06);
  text-decoration: none;
  transition: transform 180ms ease, border-color 180ms ease, background 180ms ease, box-shadow 180ms ease;
}

.frequency-science-link__icon {
  width: 42px;
  height: 42px;
  display: grid;
  place-items: center;
  color: #5d7d74;
  background: #edf5f2;
  border: 1px solid #dcebe6;
  border-radius: 50%;
}

.frequency-science-link__content {
  min-width: 0;
  display: grid;
  gap: 3px;
}

.frequency-science-link__content strong {
  color: #36495b;
  font-family: "Sora", system-ui, sans-serif;
  font-size: 0.94rem;
  font-weight: 700;
  line-height: 1.4;
}

.frequency-science-link__content > span {
  color: #748187;
  font-size: 0.84rem;
  font-weight: 500;
  line-height: 1.5;
}

.frequency-science-link__arrow {
  color: #718d85;
  font-size: 1.15rem;
  font-weight: 700;
}

.frequency-content {
  width: min(100%, 920px);
  margin: 0 auto;
  padding: 0;
  background: transparent;
  border: 0;
  border-radius: 0;
  box-shadow: none;
}

.frequency-section {
  width: 100%;
  margin: 0 0 40px;
  padding: 0 4px 40px;
  border-bottom: 1px solid rgba(97, 131, 122, 0.16);
}

          .frequency-section--last {
            margin-bottom: 0;
            padding-bottom: 0;
            border-bottom: 0;
          }

.frequency-section--soft {
  width: 100%;
  margin: 0 0 40px;
  padding: 30px 32px;
  background: rgba(245, 248, 247, 0.92);
  border: 1px solid rgba(97, 131, 122, 0.2);
  border-radius: 22px;
  box-shadow: 0 16px 38px rgba(54, 73, 91, 0.07);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

          /* Titres */

          .frequency-heading {
            margin-bottom: 18px;

            display: flex;
            align-items: center;
            gap: 13px;
          }

          .frequency-icon {
            width: 38px;
            height: 38px;
            flex: 0 0 38px;

            display: inline-grid;
            place-items: center;

            color: #5d7d74;
            background: #edf5f2;
            border: 1px solid #dcebe6;
            border-radius: 50%;
          }

          .frequency-icon--soft {
            color: #ffffff;
            background: #6f9087;
            border-color: #6f9087;
          }

          .frequency-subtitle {
            margin: 0;

            color: #36495b;
            font-family: "Sora", system-ui, sans-serif;
            font-size: 1.28rem;
            font-weight: 700;
            line-height: 1.4;
          }

          /* Textes */

          .frequency-text {
            margin: 0 0 17px;

            color: #5f6e75;
            font-size: 1rem;
            line-height: 1.8;
          }

          .frequency-text--last,
          .frequency-text:last-child {
            margin-bottom: 0;
          }

          .frequency-text strong {
            color: #445d56;
            font-weight: 700;
          }

          /* Tableaux */

.frequency-table-wrap {
  width: 100%;
  margin: 22px 0 24px;
  border: 1px solid rgba(97, 131, 122, 0.15);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.82);
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

          .frequency-table-wrap:focus-visible {
            outline: 3px solid rgba(118, 165, 167, 0.25);
            outline-offset: 3px;
          }

          .frequency-table {
            width: 100%;
            min-width: 620px;

            border-collapse: collapse;

            color: #5f6e75;
            font-size: 0.9rem;
            line-height: 1.5;
          }
          .frequency-table th,
          .frequency-table td {
            padding: 13px 15px;
            text-align: left;
            vertical-align: middle;

            border-bottom: 1px solid rgba(97, 131, 122, 0.11);
          }

          .frequency-table th {
            color: #4d6860;
            background: #edf5f2;

            font-size: 0.78rem;
            font-weight: 700;
            letter-spacing: 0.025em;
          }

          .frequency-table tbody tr:last-child td {
            border-bottom: 0;
          }

          .frequency-table tbody tr:nth-child(even) {
            background: rgba(247, 250, 249, 0.72);
          }

          .frequency-table td strong {
            color: #445d56;
          }

          .frequency-table td:nth-child(2) {
            white-space: nowrap;
          }

.frequency-table-wrap--traditions {
  width: 100%;
  margin-left: 0;
  margin-right: 0;
}

.frequency-table--traditions {
  width: 100%;
  min-width: 720px;
  table-layout: fixed;
}

.frequency-table--traditions th,
.frequency-table--traditions td {
  width: 33.333%;
  text-align: center;
  white-space: normal;
}

.frequency-table--traditions th:first-child,
.frequency-table--traditions td:first-child {
  white-space: nowrap;
}

          .frequency-list {
            margin: 22px 0 0;
            padding: 0;

            display: grid;
            gap: 12px;

            list-style: none;
          }

          .frequency-list--columns {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }

.frequency-list li {
  min-width: 0;
  padding: 15px 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: rgba(247, 250, 249, 0.86);
  border: 1px solid rgba(97, 131, 122, 0.12);
  border-radius: 14px;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

          .frequency-list strong {
            color: #4e6a62;
            font-size: 0.92rem;
            line-height: 1.4;
          }

          .frequency-list span {
            color: #748187;
            font-size: 0.85rem;
            line-height: 1.5;
          }

          /* Étapes finales */

          .frequency-steps {
            margin: 24px 0 0;
            padding: 0;

            display: grid;
            gap: 15px;

            list-style: none;
          }

          .frequency-steps li {
            display: grid;
            grid-template-columns: 34px minmax(0, 1fr);
            align-items: flex-start;
            gap: 13px;
          }

          .frequency-steps li > span {
            width: 34px;
            height: 34px;

            display: grid;
            place-items: center;

            color: #56766e;
            background: #edf5f2;
            border: 1px solid #dcebe6;
            border-radius: 50%;

            font-size: 0.82rem;
            font-weight: 700;
          }

          .frequency-steps strong {
            display: block;

            color: #445d56;
            font-size: 0.94rem;
            line-height: 1.5;
          }

          .frequency-steps p {
            margin: 3px 0 0;

            color: #748187;
            font-size: 0.88rem;
            line-height: 1.6;
          }

.frequency-closing {
  margin-top: 30px;
  padding: 22px 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #56766e;
  background: rgba(244, 248, 246, 0.88);
  border: 1px solid rgba(97, 131, 122, 0.15);
  border-radius: 17px;
  text-align: center;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

          .frequency-closing svg {
            flex: 0 0 auto;
          }

          .frequency-closing p {
            margin: 0;

            font-size: 0.94rem;
            font-weight: 600;
            line-height: 1.6;
          }

          /* Survol ordinateur */

          @media (hover: hover) and (pointer: fine) {

            .frequency-science-link:hover {
  transform: translateY(-2px);
  border-color: rgba(97, 131, 122, 0.28);
  background: #ffffff;
  box-shadow: 0 17px 36px rgba(54, 73, 91, 0.1);
}
          }

          /* Mobile */

          @media (max-width: 767px) {
            .frequences-page {
              margin-top: 94px;
              padding: 0 14px 72px;
              gap: 32px;
            }

.frequency-section {
  margin-bottom: 32px;
  padding-right: 2px;
  padding-bottom: 32px;
  padding-left: 2px;
}

            .frequency-section--last {
              margin-bottom: 0;
              padding-bottom: 0;
            }

.frequency-section--soft {
  width: 100%;
  margin: 0 0 32px;
  padding: 23px 19px;
  border-radius: 20px;
}

            .frequency-heading {
              align-items: flex-start;
              gap: 11px;
            }

            .frequency-icon {
              width: 34px;
              height: 34px;
              flex-basis: 34px;
            }

            .frequency-icon svg {
              width: 17px;
              height: 17px;
            }

            .frequency-subtitle {
              padding-top: 3px;
              font-size: 1.12rem;
            }

            .frequency-text {
              font-size: 0.96rem;
            }

            .frequency-table {
              font-size: 0.86rem;
            }

            .frequency-table th,
            .frequency-table td {
              padding: 11px 12px;
            }

            .frequency-list--columns {
              grid-template-columns: 1fr;
            }

            .frequency-closing {
              align-items: flex-start;
              padding: 19px 17px;
              text-align: left;
            }

.frequency-science-link {
  width: 100%;
  margin-top: -8px;
  padding: 15px;
  grid-template-columns: 38px minmax(0, 1fr) auto;
  gap: 11px;
  border-radius: 16px;
}

.frequency-science-link__icon {
  width: 38px;
  height: 38px;
}

.frequency-science-link__content > span {
  font-size: 0.8rem;
}

.frequency-table-wrap--traditions {
  width: 100%;
}

.frequency-table--traditions {
  min-width: 720px;
}
}


@media (prefers-reduced-motion: reduce) {
.frequency-science-link {
  transition: none;
}
          }
        `}</style>
      </main>
    </>
  );
}